import {
  ChainTypeError,
  ConnectorAccountNotFoundError,
  ConnectorNotConnectedError,
  type ChainIdParameter,
  type Connection,
  type ConnectorAccountNotFoundErrorType,
  type ConnectorNotConnectedErrorType,
  type ConnectorParameter,
  type ErrorType,
  type Evaluate,
} from '@particle-network/connector-core';
import {
  createClient,
  custom,
  type Account,
  type Address,
  type BaseErrorType,
  type Chain,
  type Client,
  type Transport,
} from 'viem';
import { getAddress, parseAccount } from 'viem/utils';
import type { Config } from '../createConfig';

export type GetConnectorClientParameters = Evaluate<
  ChainIdParameter &
    ConnectorParameter & {
      account?: Address | Account | undefined;
    }
>;

export type GetConnectorClientReturnType = Evaluate<Client<Transport, Chain, Account>>;

export type GetConnectorClientErrorType =
  | ConnectorAccountNotFoundErrorType
  | ConnectorNotConnectedErrorType
  // base
  | BaseErrorType
  | ErrorType;

export async function getConnectorClient(
  config: Config,
  parameters: GetConnectorClientParameters = {}
): Promise<GetConnectorClientReturnType> {
  // Get connection
  let connection: Connection | undefined;
  if (parameters.connector) {
    const { connector } = parameters;
    const [accounts, chainId] = await Promise.all([connector.getAccounts(), connector.getChainId()]);
    connection = {
      accounts: accounts as readonly [Address, ...Address[]],
      chainId,
      connector,
    };
  } else connection = config.state.connections.get(config.state.current!);
  if (!connection) throw new ConnectorNotConnectedError();

  const chainId = parameters.chainId ?? connection.chainId;

  type Return = GetConnectorClientReturnType;
  const connector = connection.connector;
  if (connector.chainType !== 'evm') {
    throw new ChainTypeError();
  }

  // Default using `custom` transport
  const account = parseAccount(parameters.account ?? (connection.accounts[0] as Address));
  account.address = getAddress(account.address);

  const chain = config.chains.find((chain) => chain.id === chainId);
  const provider = (await connection.connector.getProvider({ chainId })) as {
    request(...args: any): Promise<any>;
  };

  // if account was provided, check that it exists on the connector
  if (parameters.account && !connection.accounts.some((x) => x.toLowerCase() === account.address.toLowerCase()))
    throw new ConnectorAccountNotFoundError({
      address: account.address,
      connector,
    });

  return createClient({
    account,
    chain,
    name: 'Connector Client',
    transport: (opts) => custom(provider)({ ...opts, retryCount: 0 }),
  }) as Return;
}
